import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { loadSettings } from '@/config/settings';
import { drawArcadeBackground } from '@/utils/arcadeBackground';
import { NoteTrack } from '@/objects/NoteTrack';
import { HitZone } from '@/objects/HitZone';
import { VFXManager } from '@/objects/VFXManager';
import { SentTrack } from '@/objects/SentTrack';
import { InputManager } from '@/systems/InputManager';
import { ScoringSystem, type ScoreState } from '@/systems/ScoringSystem';
import { AudioManager } from '@/systems/AudioManager';
import { DIFFICULTIES, type DifficultyConfig, type HitQuality } from '@/utils/timing';
import type { MorseSymbol } from '@/utils/morse';

interface GameSceneData {
  levelIndex: number;
  message: string;
  difficulty: string;
}

export class GameScene extends Phaser.Scene {
  private levelIndex!: number;
  private message!: string;
  private difficultyConfig!: DifficultyConfig;

  // Core systems
  private noteTrack!: NoteTrack;
  private hitZone!: HitZone;
  private inputManager!: InputManager;
  private scoringSystem!: ScoringSystem;
  private vfxManager!: VFXManager;
  private audioManager!: AudioManager;
  private sentTrack!: SentTrack;
  private difficultyKey: string = 'medium';

  // UI elements
  private scoreText!: Phaser.GameObjects.Text;
  private streakText!: Phaser.GameObjects.Text;
  private multiplierText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;

  // Game state
  private isPlaying: boolean = false;
  private gameStartTime: number = 0;
  private notesFinished: boolean = false;
  private notesFinishedTime: number = 0;
  private inputDisabled: boolean = false;
  private inputDisabledTime: number = 0;
  private readonly INPUT_GRACE_PERIOD = 2000;  // 2 seconds after last note before disabling input

  // Track layout constants
  private readonly HIT_ZONE_X = 640;  // Center of screen
  private readonly TRACK_Y = GAME_HEIGHT / 2 + 40;  // 400
  private readonly MESSAGE_Y = GAME_HEIGHT / 2 - 40;  // 320 - TRANSMITTED/TARGET values line
  private readonly LABEL_Y = GAME_HEIGHT / 2 - 80;  // 280 - labels above values
  private readonly FADE_END_X = 100;  // Left boundary where symbols fade
  private readonly ACCUMULATED_START_X = 50;  // Where decoded letters stop

  // Track boundaries for clipping
  private readonly TRACK_X = 40;
  private readonly TRACK_WIDTH = GAME_WIDTH - 80;  // 1200
  private readonly TRACK_HEIGHT = 110;

  private trackMask!: Phaser.Display.Masks.GeometryMask;

  constructor() {
    super({ key: 'GameScene' });
  }

  init(data: GameSceneData): void {
    this.levelIndex = data.levelIndex ?? 0;
    this.message = data.message || 'SOS';
    this.difficultyKey = data.difficulty || 'easy';
    this.difficultyConfig = DIFFICULTIES[this.difficultyKey] || DIFFICULTIES.easy;

    // Reset game state
    this.isPlaying = false;
    this.gameStartTime = 0;
    this.notesFinished = false;
    this.notesFinishedTime = 0;
    this.inputDisabled = false;
    this.inputDisabledTime = 0;
  }

  create(): void {
    // Fade in
    this.cameras.main.fadeIn(300, 5, 7, 22);

    // Create background
    this.createBackground();

    // Create track visual
    this.createTrack();

    // Create hit zone
    this.hitZone = new HitZone(this, {
      x: this.HIT_ZONE_X,
      y: this.TRACK_Y,
      width: 60,
      height: 70,
    });

    // Create HUD
    this.createHUD();

    // Create input manager first (needed for key hints)
    this.inputManager = new InputManager(this);

    // Create note track with mask for clipping at track boundaries
    this.noteTrack = new NoteTrack(this, {
      message: this.message,
      difficulty: this.difficultyConfig,
      hitZoneX: this.HIT_ZONE_X,
      trackY: this.TRACK_Y,
      mask: this.trackMask,
    });

    // Create scoring system
    this.scoringSystem = new ScoringSystem(
      this.difficultyConfig,
      this.noteTrack.getTotalNotes()
    );
    this.setupScoringCallbacks();

    // Create VFX and Audio managers
    this.vfxManager = new VFXManager(this);
    this.audioManager = new AudioManager(this);

    // Create sent track (symbols scrolling left from hit zone) with mask for clipping
    this.sentTrack = new SentTrack(this, {
      hitZoneX: this.HIT_ZONE_X,
      trackY: this.TRACK_Y,
      letterY: this.MESSAGE_Y,
      scrollSpeed: this.difficultyConfig.scrollSpeed,
      fadeEndX: this.FADE_END_X,
      stopX: this.ACCUMULATED_START_X,
      mask: this.trackMask,
    });

    // Set up input callback
    this.inputManager.onInput((symbol, time) => this.handleInput(symbol, time));

    // Show countdown then start
    this.showCountdown();

    // ESC to quit
    this.input.keyboard?.on('keydown-ESC', () => {
      this.endGame();
    });
  }

  private createBackground(): void {
    // Subtle arcade background (reduced for gameplay)
    drawArcadeBackground(this);

    // Top HUD strip
    const hudBar = this.add.rectangle(GAME_WIDTH / 2, 60, GAME_WIDTH, 120, THEME.colors.backgroundLightHex, 0.68);
    hudBar.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    hudBar.setDepth(-1);

    // Bottom strip for key hints
    const footer = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT - 40, GAME_WIDTH, 80, THEME.colors.backgroundLightHex, 0.68);
    footer.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    footer.setDepth(-1);

    // Level message at top left
    this.add.text(20, 20, this.message, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0, 0);

    this.add.text(20, 45, `${this.difficultyConfig.name} Mode`, {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.textMuted,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0);

    // DECODED label and value (left side, close to track)
    this.add.text(40, this.LABEL_Y, 'TRANSMITTED:', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    });
    // Note: decoded value is rendered by SentTrack as letters stop scrolling

    // TARGET label and value (right side, same Y positions)
    this.add.text(GAME_WIDTH - 40, this.LABEL_Y, 'TARGET:', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(1, 0);

    this.add.text(GAME_WIDTH - 40, this.MESSAGE_Y, this.message, {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.heading,
      color: THEME.colors.primary,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(1, 0.5);
  }

  private createTrack(): void {
    const graphics = this.add.graphics();

    const trackHeight = this.TRACK_HEIGHT;
    const trackY = this.TRACK_Y - trackHeight / 2;
    const trackWidth = this.TRACK_WIDTH;
    const trackX = this.TRACK_X;

    // Create mask shape for clipping notes to track bounds
    const maskShape = this.make.graphics({ x: 0, y: 0 });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRoundedRect(trackX, trackY, trackWidth, trackHeight, 14);
    this.trackMask = maskShape.createGeometryMask();

    // Neon gradient track with magenta rails
    graphics.fillGradientStyle(
      THEME.colors.backgroundLightHex,
      THEME.colors.backgroundLightHex,
      THEME.colors.backgroundPanelHex,
      THEME.colors.backgroundPanelHex,
      0.95,
      0.95,
      0.9,
      0.9
    );
    graphics.fillRoundedRect(trackX, trackY, trackWidth, trackHeight, 14);

    graphics.lineStyle(3, THEME.colors.magentaHex, 0.55);
    graphics.strokeRoundedRect(trackX, trackY, trackWidth, trackHeight, 14);

    graphics.lineStyle(1, THEME.colors.backgroundPanelHex);
    graphics.lineBetween(trackX, this.TRACK_Y, trackX + trackWidth, this.TRACK_Y);

    // Directional chevrons pointing right-to-left
    // Left-pointing chevrons with a simple gap around the hit zone
    graphics.fillStyle(THEME.colors.cyanHex, 0.25);
    const chevronSpacing = 120;
    const chevronHalfWidth = 28;
    const chevronInset = 12;
    const margin = 70;
    const gapHalf = 80;

    const gapStart = this.HIT_ZONE_X - gapHalf;
    const gapEnd = this.HIT_ZONE_X + gapHalf;
    const leftLimit = trackX + margin;
    const rightLimit = trackX + trackWidth - margin;

    for (let i = 0; ; i++) {
      const offset = chevronSpacing / 2 + i * chevronSpacing;
      const leftCx = gapStart - offset;
      const rightCx = gapEnd + offset;

      if (leftCx < leftLimit || rightCx > rightLimit) {
        break;
      }

      graphics.fillTriangle(
        leftCx - chevronHalfWidth,
        this.TRACK_Y,
        leftCx + chevronHalfWidth,
        trackY + chevronInset,
        leftCx + chevronHalfWidth,
        trackY + trackHeight - chevronInset
      );

      graphics.fillTriangle(
        rightCx - chevronHalfWidth,
        this.TRACK_Y,
        rightCx + chevronHalfWidth,
        trackY + chevronInset,
        rightCx + chevronHalfWidth,
        trackY + trackHeight - chevronInset
      );
    }

    // Hit lane highlights near hit zone
    graphics.lineStyle(2, THEME.colors.cyanHex, 0.5);
    graphics.strokeRoundedRect(this.HIT_ZONE_X - 60, trackY + 8, 120, trackHeight - 16, 10);
  }

  private createHUD(): void {
    // Score (top center)
    this.scoreText = this.add.text(GAME_WIDTH / 2, 20, 'SCORE: 0', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.subheading,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5, 0);

    // Streak
    this.streakText = this.add.text(GAME_WIDTH / 2, 55, 'STREAK: 0', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.body,
      color: THEME.colors.primary,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5, 0);

    // Multiplier
    this.multiplierText = this.add.text(GAME_WIDTH / 2, 85, 'x1', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5, 0);

    // Feedback text - above hit zone at center
    this.feedbackText = this.add.text(this.HIT_ZONE_X, this.TRACK_Y - 80, '', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.primary,
    }).setOrigin(0.5);

    // Key hints - bottom center (use actual configured keys)
    const settings = loadSettings();
    const keyHintContainer = this.add.container(GAME_WIDTH / 2, GAME_HEIGHT - 52);
    const dotText = this.add.text(-100, 0, `[${settings.dotKey}] / LMB`, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(1, 0.5);
    const dotIcon = this.add.image(-90, 0, 'dot-sprite').setOrigin(0, 0.5).setScale(0.7);
    const dashText = this.add.text(70, 0, `[${settings.dashKey}] / RMB`, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
    }).setOrigin(1, 0.5);
    const dashIcon = this.add.image(80, 0, 'dash-sprite').setOrigin(0, 0.5).setScale(0.7);
    keyHintContainer.add([dotText, dotIcon, dashText, dashIcon]);

    // ESC hint in corner
    this.add.text(GAME_WIDTH - 20, GAME_HEIGHT - 20,
      'ESC = quit', {
        fontFamily: THEME.fonts.body,
        fontSize: THEME.sizes.small,
        color: THEME.colors.text,
      }).setOrigin(1, 0.5);
  }

  private setupScoringCallbacks(): void {
    this.scoringSystem.setOnScoreChange((state: ScoreState) => {
      this.updateHUD(state);
    });

    this.scoringSystem.setOnStreakMilestone((streak: number) => {
      this.showStreakMilestone(streak);
    });
  }

  private showCountdown(): void {
    const countdownText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 50, '3', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.titleLarge,
      color: THEME.colors.primary,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThick,
    }).setOrigin(0.5);

    let count = 3;
    const countdown = this.time.addEvent({
      delay: 1000,
      callback: () => {
        count--;
        if (count > 0) {
          countdownText.setText(count.toString());
          this.tweens.add({
            targets: countdownText,
            scale: { from: 1.5, to: 1 },
            duration: 500,
          });
        } else if (count === 0) {
          countdownText.setText('GO!');
          this.tweens.add({
            targets: countdownText,
            scale: { from: 1.5, to: 1 },
            alpha: 0,
            duration: 500,
            onComplete: () => countdownText.destroy(),
          });
        } else {
          countdown.destroy();
          this.startGame();
        }
      },
      repeat: 3,
    });
  }

  private startGame(): void {
    this.isPlaying = true;
    this.gameStartTime = this.time.now;
    this.noteTrack.start(this.gameStartTime);
    this.sentTrack.start();
    this.inputManager.enable();
  }

  update(time: number, delta: number): void {
    if (!this.isPlaying) return;

    this.noteTrack.update(time, delta);
    this.sentTrack.update(time, delta);
    this.checkMissedNotes();

    // When all incoming notes are done, start the grace period
    if (this.noteTrack.isComplete() && !this.notesFinished) {
      this.notesFinished = true;
      this.notesFinishedTime = time;
    }

    // After grace period, disable input
    if (this.notesFinished && !this.inputDisabled) {
      const timeSinceNotesFinished = time - this.notesFinishedTime;
      if (timeSinceNotesFinished > this.INPUT_GRACE_PERIOD) {
        this.inputDisabled = true;
        this.inputDisabledTime = time;
        this.inputManager.disable();

        // Finalize any pending character being typed
        this.sentTrack.forceFinalize();
      }
    }

    // After input disabled, wait for sent track to clear out before ending
    if (this.inputDisabled) {
      // Give enough time for all sent symbols to scroll through and decode
      // Wait at least 3 seconds after input disabled
      const timeSinceInputDisabled = time - this.inputDisabledTime;
      if (timeSinceInputDisabled > 3000 && this.sentTrack.isEmpty()) {
        this.endGame();
      }
    }
  }

  private handleInput(symbol: MorseSymbol, time: number): void {
    if (!this.isPlaying) return;

    // Play input sound
    if (symbol === '.') {
      this.audioManager.playDot();
    } else {
      this.audioManager.playDash();
    }

    // Add symbol to sent track (decoding is purely based on player input timing)
    this.sentTrack.addSymbol(symbol, time);

    // Check if there's a note to score against
    const note = this.noteTrack.getNextNote();
    if (!note) {
      // No note to hit - show miss feedback
      this.showHitFeedback('miss', symbol);
      return;
    }

    const previousStreak = this.scoringSystem.getState().streak;
    const pixelOffset = note.getPositionOffset();
    const quality = this.scoringSystem.processHit(
      pixelOffset,
      symbol,
      note.symbol
    );

    // Play hit sound and show VFX
    this.audioManager.playHit(quality as HitQuality);
    this.showHitFeedback(quality, symbol);
    this.vfxManager.showHitEffect(this.HIT_ZONE_X, this.TRACK_Y, quality as HitQuality);

    if (quality !== 'miss') {
      note.markHit();
      const newStreak = this.scoringSystem.getState().streak;
      this.hitZone.updateStreak(newStreak);
      this.vfxManager.updateStreakEffect(newStreak, this.HIT_ZONE_X, this.TRACK_Y);
    } else {
      // Streak broken
      if (previousStreak >= 10) {
        this.vfxManager.showComboBreak();
        this.audioManager.playStreakBreak();
      }
      this.hitZone.flashMiss();
      this.hitZone.reset();
      this.vfxManager.updateStreakEffect(0, this.HIT_ZONE_X, this.TRACK_Y);
    }
  }

  private checkMissedNotes(): void {
    const note = this.noteTrack.getNextNote();
    if (note && note.hasPassed() && note.state !== 'hit') {
      const previousStreak = this.scoringSystem.getState().streak;
      this.scoringSystem.processMiss();

      // Show miss effects
      this.vfxManager.showMissEffect(this.HIT_ZONE_X, this.TRACK_Y);
      this.audioManager.playMiss();

      if (previousStreak >= 10) {
        this.vfxManager.showComboBreak();
        this.audioManager.playStreakBreak();
      }

      this.hitZone.flashMiss();
      this.hitZone.reset();
      this.vfxManager.updateStreakEffect(0, this.HIT_ZONE_X, this.TRACK_Y);
    }
  }

  private showHitFeedback(quality: string, symbol: MorseSymbol): void {
    const feedbackColors: Record<string, { text: string; color: string }> = {
      perfect: { text: 'PERFECT!', color: THEME.colors.hit.perfect },
      good: { text: 'GOOD!', color: THEME.colors.hit.good },
      ok: { text: 'OK', color: THEME.colors.hit.ok },
      miss: { text: 'MISS', color: THEME.colors.hit.miss },
    };

    const feedback = feedbackColors[quality] || feedbackColors.miss;

    this.feedbackText.setText(feedback.text);
    this.feedbackText.setColor(feedback.color);
    this.feedbackText.setScale(1.2);
    this.feedbackText.setAlpha(1);

    this.tweens.add({
      targets: this.feedbackText,
      scale: 1,
      alpha: 0,
      duration: 400,
    });

    switch (quality) {
      case 'perfect':
        this.hitZone.flashSuccess();
        break;
      case 'good':
        this.hitZone.flashGood();
        break;
      case 'ok':
        this.hitZone.flashOk();
        break;
    }

    const symbolText = this.add.text(
      this.HIT_ZONE_X,
      this.TRACK_Y,
      symbol === '.' ? '.' : '-',
      {
        fontFamily: THEME.fonts.mono,
        fontSize: THEME.sizes.title,
        color: feedback.color,
      }
    ).setOrigin(0.5);

    this.tweens.add({
      targets: symbolText,
      y: symbolText.y - 40,
      alpha: 0,
      duration: 300,
      onComplete: () => symbolText.destroy(),
    });
  }

  private showStreakMilestone(streak: number): void {
    // Play milestone sound
    this.audioManager.playStreakMilestone(streak);

    // Show milestone VFX
    this.vfxManager.showStreakMilestone(streak, GAME_WIDTH / 2, GAME_HEIGHT / 2);

    const milestoneText = this.add.text(
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2 - 100,
      `${streak} STREAK!`,
      {
        fontFamily: THEME.fonts.pixelBold,
        fontSize: THEME.sizes.title,
        color: THEME.colors.cyan,
        stroke: THEME.stroke.color,
        strokeThickness: THEME.stroke.thickness,
      }
    ).setOrigin(0.5);

    this.tweens.add({
      targets: milestoneText,
      scale: { from: 0.5, to: 1.2 },
      alpha: { from: 1, to: 0 },
      duration: 1000,
      ease: 'Back.easeOut',
      onComplete: () => milestoneText.destroy(),
    });
  }

  private updateHUD(state: ScoreState): void {
    this.scoreText.setText(`SCORE: ${state.score.toLocaleString()}`);
    this.streakText.setText(`STREAK: ${state.streak}`);

    const multiplierColors: Record<number, string> = {
      1: THEME.colors.textDark,
      2: THEME.colors.yellow,
      3: THEME.colors.cyan,
      4: THEME.colors.magenta,
    };
    this.multiplierText.setText(`x${state.multiplier}`);
    this.multiplierText.setColor(multiplierColors[state.multiplier] || THEME.colors.text);

    if (state.multiplier > 1) {
      this.tweens.add({
        targets: this.multiplierText,
        scale: { from: 1.3, to: 1 },
        duration: 200,
      });
    }
  }

  private endGame(): void {
    this.isPlaying = false;
    this.inputManager.disable();

    const results = this.scoringSystem.getResults();

    // Use the sent track's decoded message
    const decodedMessage = this.sentTrack.getDecodedMessage();

    this.cameras.main.fadeOut(300, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('ResultScene', {
        score: results.score,
        streak: results.maxStreak,
        decodedMessage: decodedMessage,
        intendedMessage: this.message,
        accuracy: results.accuracy,
        perfectHits: results.perfectHits,
        goodHits: results.goodHits,
        okHits: results.okHits,
        misses: results.misses,
        levelIndex: this.levelIndex,
        difficultyKey: this.difficultyKey,
      });
    });
  }

  shutdown(): void {
    this.noteTrack?.destroy();
    this.sentTrack?.destroy();
    this.inputManager?.destroy();
    this.vfxManager?.destroy();
    this.audioManager?.destroy();
  }
}
